import SpeechBubble from '@design-system/components/molecules/SpeechBubble'
import { Icon } from '@design-system/index'
import styled from '@emotion/styled'
import { colors, typo, zIndex } from '@mathflat/common/style'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { s3URL } from '@mathflat/common/index'
import clsx from 'clsx'
import { addDays, isAfter, isBefore } from 'date-fns'
import { observer } from 'mobx-react'
import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router'

import { CustomEventName, getContractUrl } from '@/constant'
import type { Banner } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import { getMyPageNavigation } from '@/router/constant'
import { envUtils } from '@/utils/envUtils'
import BannerService from '@/service/banner.service'
import { localStorageService } from '@/service/storage.service'
import { openLink } from '@/utils/common'
import { mixpanelClient } from '@/utils/trackings/mixpanel'
import { Portal } from '../Portal'
import TooltipBanner from './TooltipBanner'
import WebviewService from '@/service/webview.service'

const Profile = () => {
  return <_Profile />
}

const _Profile = observer(() => {
  const ref = useRef<HTMLButtonElement>(null)
  const rect = ref.current?.getBoundingClientRect()
  const history = useHistory()
  const { path } = useRouteMatch()

  const [isNavigationOpened, setIsNavigationOpened] = useState(false)

  useEffect(() => {
    const handleNavigationOpened = () => {
      setIsNavigationOpened(false)
    }
    document.addEventListener('click', handleNavigationOpened)

    return () => {
      document.removeEventListener('click', handleNavigationOpened)
    }
  }, [])

  const navInfoList = useMemo(
    () =>
      getMyPageNavigation(commonRepo.permission.get, {
        mathflatConnect: () => {
          window.open(
            envUtils.getMathflatBaseUrl(),
            WebviewService.isAppAndroid ? '_self' : '_blank',
          )
        },
      }),
    [commonRepo.permission.get],
  )

  const Badge = (() => {
    if (commonRepo.permission.hasPremiumAuth) {
      return (
        <div className="profile__star-wrapper">
          <Icon className="profile__star-icon" name="star_fill" size={10} color="white" />
        </div>
      )
    }
    return (
      <Icon className="profile__profile-icon" name="profile" size={20} color={colors.gray.$400} />
    )
  })()

  return (
    <button
      className={clsx(
        'header__profile',
        path.includes(RouteName.myInfo) && 'active',
        isNavigationOpened && 'opened',
      )}
      ref={ref}
      onClick={(e) => {
        e.stopPropagation()
        mixpanelClient.track('GNB_마이페이지버튼_Click', {
          펼침여부: isNavigationOpened,
        })
        setIsNavigationOpened(!isNavigationOpened)
      }}
    >
      <Portal.Element
        isVisible={isNavigationOpened}
        rect={rect}
        offset={{ x: rect?.width ?? 0, y: 0 }}
      >
        <ProfilePopover>
          {navInfoList.map((navInfo, index) => {
            if (!navInfo) return null
            return (
              <React.Fragment key={index}>
                {navInfo.title && <div className="profile__popover-title">{navInfo.title}</div>}
                <div className="profile__popover-group">
                  {navInfo.items.map((item) => {
                    if (!item) return null

                    return (
                      <button
                        key={item.label ?? String(item.labelContent)}
                        className="profile__popover-item"
                        disabled={item.disabled}
                        onClick={() => {
                          if (item.action) {
                            item.action()
                          } else if (item.to) {
                            history.push(item.to)
                          }
                        }}
                      >
                        {item.labelContent ?? item.label}
                        {item.isNew && (
                          <div className="new-badge">
                            <img
                              src={s3URL.mathflatAssetsBucket.graphic('graphic-new.svg')}
                              alt="new"
                              width={14}
                              height={14}
                            />
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              </React.Fragment>
            )
          })}
        </ProfilePopover>
      </Portal.Element>
      {Badge}
      <span className="profile__teacher-nickname">{commonRepo.user?.name}</span>
      <div className="profile__chevron-wrapper">
        <Icon name="chevron_down" size={20} color="var(--Colors-Gray-700)" />
      </div>
    </button>
  )
})
export default observer(Profile)

const ProfilePopover = styled.nav`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 220px;
  top: 41px;
  right: 0;
  border-radius: var(--Radius-200);
  border: 1px solid var(--Colors-Gray-300);
  background: var(--White);
  box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: ${zIndex.PopperNavigation};
  overflow: hidden;

  .profile__popover-title {
    ${typo.caption01};
    font-weight: 400;
    color: var(--Colors-Gray-600);
    padding: 14px 16px;
  }

  .profile__popover-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-bottom: 1px solid var(--Colors-Gray-300);
  }

  .profile__popover-item {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 14px 16px;
    ${typo.body02};
    color: var(--Colors-Gray-900);
    font-weight: 400;
    text-align: left;

    @media (hover: hover) {
      &:hover {
        background-color: var(--Colors-Gray-100);
      }
    }

    &:active {
      background-color: var(--Colors-Gray-300);
    }

    &:disabled {
      cursor: not-allowed;

      &:hover,
      &:active {
        background-color: transparent;
      }
    }

    .new-badge {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 14px;
      height: 14px;
      border-radius: var(--Radius-100);
      background: var(--Colors-Orange-500);
    }
  }

  .profile__popover-divider {
    height: 1px;
    background-color: ${colors.gray.$300};
  }
`
